import React, { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Shield,
  Package,
  X,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Pagination,
} from '../components/ui'
import { LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import {
  useFeatures,
  useLicenses,
  useCreateFeature,
  useUpdateFeature,
  useDeleteFeature,
  useCreateLicense,
  useUpdateLicense,
  useDeleteLicense,
} from '../hooks/useLicensing'
import type { Feature, License } from '../types/licensing.types'
import { useAuth } from '../providers/AuthProvider'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 2, staleTime: 30_000 },
  },
})

// ========== Feature Dialog ==========

function FeatureDialog({
  open,
  onClose,
  feature,
}: {
  open: boolean
  onClose: () => void
  feature?: Feature | null
}) {
  const createFeature = useCreateFeature()
  const updateFeature = useUpdateFeature()

  const [form, setForm] = useState({
    name: feature?.name || '',
    display_name: feature?.display_name || '',
    description: feature?.description || '',
    is_active: feature?.is_active ?? true,
  })

  React.useEffect(() => {
    if (open) {
      setForm({
        name: feature?.name || '',
        display_name: feature?.display_name || '',
        description: feature?.description || '',
        is_active: feature?.is_active ?? true,
      })
    }
  }, [open, feature])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (feature) {
        await updateFeature.mutateAsync({ id: feature.id, data: form })
      } else {
        await createFeature.mutateAsync(form)
      }
      onClose()
    } catch (err: any) {
      alert(err?.message || 'Error saving feature')
    }
  }

  if (!open) return null

  const isLoading = createFeature.isPending || updateFeature.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background rounded-lg shadow-lg w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {feature ? 'Редактировать фичу' : 'Новая фича'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">Код (slug)</label>
            <Input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder=""
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium">Название</label>
            <Input
              value={form.display_name}
              onChange={(e) => setForm((p) => ({ ...p, display_name: e.target.value }))}
              placeholder=""
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium">Описание</label>
            <Input
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Описание сервиса..."
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="feature-active"
              checked={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              className="rounded"
            />
            <label htmlFor="feature-active" className="text-sm">Активна</label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Отмена</Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ========== License Dialog ==========

function LicenseDialog({
  open,
  onClose,
  license,
  features,
}: {
  open: boolean
  onClose: () => void
  license?: License | null
  features: Feature[]
}) {
  const createLicense = useCreateLicense()
  const updateLicense = useUpdateLicense()

  const [form, setForm] = useState({
    bin: license?.bin || '',
    organization_name: license?.organization_name || '',
    feature_id: license?.feature_id || '',
    expires_at: license?.expires_at ? license.expires_at.slice(0, 10) : '',
    is_active: license?.is_active ?? true,
  })

  React.useEffect(() => {
    if (open) {
      setForm({
        bin: license?.bin || '',
        organization_name: license?.organization_name || '',
        feature_id: license?.feature_id || (features.length > 0 ? features[0].id : ''),
        expires_at: license?.expires_at ? license.expires_at.slice(0, 10) : '',
        is_active: license?.is_active ?? true,
      })
    }
  }, [open, license, features])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const payload = {
        ...form,
        expires_at: new Date(form.expires_at).toISOString(),
      }
      if (license) {
        await updateLicense.mutateAsync({ id: license.id, data: payload })
      } else {
        await createLicense.mutateAsync(payload)
      }
      onClose()
    } catch (err: any) {
      alert(err?.message || 'Error saving license')
    }
  }

  if (!open) return null

  const isLoading = createLicense.isPending || updateLicense.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background rounded-lg shadow-lg w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {license ? 'Редактировать лицензию' : 'Новая лицензия'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">БИН организации</label>
            <Input
              value={form.bin}
              onChange={(e) => setForm((p) => ({ ...p, bin: e.target.value }))}
              placeholder="120140000842"
              pattern="\d{12}"
              maxLength={12}
              required
            />
            <p className="text-xs text-muted-foreground mt-1">12 цифр</p>
          </div>
          <div>
            <label className="text-sm font-medium">Наименование организации</label>
            <Input
              value={form.organization_name}
              onChange={(e) => setForm((p) => ({ ...p, organization_name: e.target.value }))}
              placeholder='ТОО "Астана"'
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium">Фича</label>
            <select
              value={form.feature_id}
              onChange={(e) => setForm((p) => ({ ...p, feature_id: e.target.value }))}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              required
            >
              <option value="">Выберите фичу</option>
              {features.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.display_name} ({f.name})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Срок действия</label>
            <Input
              type="date"
              value={form.expires_at}
              onChange={(e) => setForm((p) => ({ ...p, expires_at: e.target.value }))}
              required
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="license-active"
              checked={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              className="rounded"
            />
            <label htmlFor="license-active" className="text-sm">Активна</label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Отмена</Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ========== Features Tab ==========

function FeaturesTab() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editFeature, setEditFeature] = useState<Feature | null>(null)

  const { data, isLoading, error, refetch } = useFeatures({
    search: search || undefined,
    page,
    limit: 20,
  })
  const deleteFeature = useDeleteFeature()

  const handleDelete = async (feature: Feature) => {
    if (!confirm(`Удалить фичу "${feature.display_name}"?`)) return
    try {
      await deleteFeature.mutateAsync(feature.id)
    } catch (err: any) {
      alert(err?.message || 'Ошибка удаления')
    }
  }

  if (error) {
    return <ErrorState title="Ошибка загрузки фич" onAction={refetch} />
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="pl-10"
          />
        </div>
        <Button onClick={() => { setEditFeature(null); setDialogOpen(true) }}>
          <Plus className="mr-2 h-4 w-4" />
          Добавить фичу
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6"><LoadingTable rows={5} /></div>
          ) : data && data.data.length > 0 ? (
            <div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Код</TableHead>
                    <TableHead>Название</TableHead>
                    <TableHead>Описание</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((feature) => (
                    <TableRow key={feature.id}>
                      <TableCell>
                        <code className="text-sm bg-muted px-1.5 py-0.5 rounded">{feature.name}</code>
                      </TableCell>
                      <TableCell className="font-medium">{feature.display_name}</TableCell>
                      <TableCell className="text-muted-foreground text-sm max-w-[300px] truncate">
                        {feature.description || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={feature.is_active ? 'default' : 'secondary'}>
                          {feature.is_active ? 'Активна' : 'Неактивна'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEditFeature(feature); setDialogOpen(true) }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(feature)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {data.totalPages > 1 && (
                <div className="flex justify-center p-4">
                  <Pagination currentPage={data.page} totalPages={data.totalPages} onPageChange={setPage} />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">Фичи не найдены</h3>
              <p className="mt-1 text-sm text-muted-foreground">Добавьте первую фичу.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <FeatureDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditFeature(null) }}
        feature={editFeature}
      />
    </div>
  )
}

// ========== Licenses Tab ==========

function LicensesTab() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editLicense, setEditLicense] = useState<License | null>(null)

  const { data, isLoading, error, refetch } = useLicenses({
    search: search || undefined,
    page,
    limit: 20,
  })
  const { data: featuresData } = useFeatures({ limit: 100 })
  const deleteLicense = useDeleteLicense()

  const isExpired = (expiresAt: string) => new Date(expiresAt) < new Date()

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  const handleDelete = async (license: License) => {
    if (!confirm(`Удалить лицензию для "${license.organization_name}"?`)) return
    try {
      await deleteLicense.mutateAsync(license.id)
    } catch (err: any) {
      alert(err?.message || 'Ошибка удаления')
    }
  }

  if (error) {
    return <ErrorState title="Ошибка загрузки лицензий" onAction={refetch} />
  }

  const features = featuresData?.data || []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск по БИН или названию..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="pl-10"
          />
        </div>
        <Button onClick={() => { setEditLicense(null); setDialogOpen(true) }}>
          <Plus className="mr-2 h-4 w-4" />
          Добавить лицензию
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6"><LoadingTable rows={5} /></div>
          ) : data && data.data.length > 0 ? (
            <div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>БИН</TableHead>
                    <TableHead>Организация</TableHead>
                    <TableHead>Фича</TableHead>
                    <TableHead>Срок действия</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((license) => {
                    const expired = isExpired(license.expires_at)
                    return (
                      <TableRow key={license.id}>
                        <TableCell>
                          <code className="text-sm">{license.bin}</code>
                        </TableCell>
                        <TableCell className="font-medium">{license.organization_name}</TableCell>
                        <TableCell>
                          {license.features ? (
                            <Badge variant="outline">{license.features.display_name}</Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <span className={expired ? 'text-destructive font-medium' : ''}>
                            {formatDate(license.expires_at)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {!license.is_active ? (
                            <Badge variant="secondary">Неактивна</Badge>
                          ) : expired ? (
                            <Badge variant="destructive">Истекла</Badge>
                          ) : (
                            <Badge variant="default">Активна</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center gap-1 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setEditLicense(license); setDialogOpen(true) }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(license)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
              {data.totalPages > 1 && (
                <div className="flex justify-center p-4">
                  <Pagination currentPage={data.page} totalPages={data.totalPages} onPageChange={setPage} />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <Shield className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">Лицензии не найдены</h3>
              <p className="mt-1 text-sm text-muted-foreground">Добавьте первую лицензию.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <LicenseDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditLicense(null) }}
        license={editLicense}
        features={features}
      />
    </div>
  )
}

// ========== Main Page ==========

function LicensingContent() {
  const [tab, setTab] = useState<'features' | 'licenses'>('licenses')

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-[1200px] mx-auto p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Лицензирование</h1>
          <p className="text-muted-foreground">
            Управление лицензиями организаций и фичами сервисов
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          <button
            onClick={() => setTab('licenses')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === 'licenses'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Shield className="inline-block mr-2 h-4 w-4" />
            Лицензии
          </button>
          <button
            onClick={() => setTab('features')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === 'features'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Package className="inline-block mr-2 h-4 w-4" />
            Фичи
          </button>
        </div>

        {/* Tab content */}
        {tab === 'features' ? <FeaturesTab /> : <LicensesTab />}
      </div>
    </div>
  )
}

export function Licensing() {
  const { user } = useAuth()

  // Реестр лицензий кросс-тенантный: на бэкенде он закрыт SuperAdminGuard.
  // Раньше страница была публичной, как и сам API. Тот же приём защиты, что
  // и на странице SuperAdmin.
  if (user && !user.isSuperadmin) {
    return <Navigate to="/" replace />
  }

  return (
    <QueryClientProvider client={queryClient}>
      <LicensingContent />
    </QueryClientProvider>
  )
}
